import { toSupportedJSONPickerType } from '@portal/components'
import * as Sentry from '@sentry/browser'
import { SystemCollections } from 'components/dittoAppConnection'
import { useRPCClient } from 'components/hooks/rpc'
import dayjs, { Dayjs } from 'dayjs'
import utc from 'dayjs/plugin/utc'
import getProperty from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import { useCallback, useEffect } from 'react'
import { gunzip, LegacyHTTPDocument } from 'utils'

dayjs.extend(utc)

type Params = {
  /**
   * The ID of the app to fetch devices for
   */
  appId: string
  /**
   * The cloud domain URL for the app
   */
  cloudUrl?: string
}

export type Return = {
  /**
   * The access token used for RPC requests. This token will be used internally
   * via the `useRpcClient` hook, so you don't need to use it directly. It is provided
   * to ensure that the token is defined _before_ attempting to use any of the RPC
   * operations.
   */
  accessToken?: string
  /**
   * Updates the logs request timestamp for a list of devices by their peer keys
   */
  bulkRequestLogs: (peerKeys: string[]) => Promise<void>
  /**
   * Fetches all devices
   *
   * @throws If an error occurred while fetching the devices
   */
  getDevices: () => Promise<Device[]>
  /**
   * Fetches a device by its ID (peer key)
   *
   * @param peerKey The ID of the device to fetch
   * @returns The device with the given ID, or null if no device was found
   * @throws If an error occurred while fetching the device
   */
  getDeviceByPeerKey: (peerKey: string) => Promise<Device | null>
  /**
   * Fetches the logs for a device and ungzips the data
   *
   * @param attachmentId The ID of the attachment to fetch
   * @returns An array of arrays of bytes representing the attachment data, or null
   * @throws If an error occurred while fetching the logs
   */
  fetchLogs: (attachmentId: string) => Promise<Uint8Array[] | null>
  /**
   * A callback to refresh the RPC access token used for the underlying RPC operations
   */
  refreshToken: () => Promise<void>
  /**
   * Updates the logs request timestamp for a device by its peer key
   */
  requestLogs: (peerKey: string) => Promise<void>
}

/**
 * A hook that provides device dashboard data and log requests operations,
 * using the RPC client
 */
export default function useDeviceRPC({ appId, cloudUrl }: Params): Return {
  const {
    rpcAccessToken,
    refreshToken,
    executeLegacyFindQuery,
    executeLegacyFindByIdQuery,
    executeLegacyUpdate,
    downloadAttachment,
  } = useRPCClient(appId, cloudUrl)

  useEffect(() => {
    Sentry.addBreadcrumb({
      level: 'debug',
      data: {
        appId,
      },
    })
  }, [appId])

  const getDevices = useCallback(async () => {
    const response = await executeLegacyFindQuery({
      query: 'true',
      collection: SystemCollections.SMALL_PEER_INFO_COLLECTION,
      sort: [{ property: 'last_updated_at', direction: 'desc' }],
      options: {
        formatAttachment: true,
      },
    })

    return response.documents.map(resultToDevice)
  }, [executeLegacyFindQuery])

  const getDeviceByPeerKey = useCallback(
    async (peerKey: string) => {
      const response = await executeLegacyFindByIdQuery({
        id: peerKey,
        collection: SystemCollections.SMALL_PEER_INFO_COLLECTION,
        options: {
          formatAttachment: true,
        },
      })

      return response ? resultToDevice(response) : null
    },
    [executeLegacyFindByIdQuery],
  )

  const fetchLogs = useCallback(
    async (attachmentId: string) => {
      try {
        const bytes = await downloadAttachment(attachmentId)
        if (!bytes) {
          Sentry.captureMessage(
            `No bytes received for attachment ID: ${attachmentId}`,
            {
              level: 'info',
            },
          )
          return null
        }

        const uncompressedMembers = gunzip(bytes)
        const totalUncompressedSize = uncompressedMembers.reduce(
          (total, member) => total + member.length,
          0,
        )

        if (!totalUncompressedSize) {
          Sentry.captureMessage(
            `Received empty file for attachment ID: ${attachmentId}`,
            {
              level: 'warning',
            },
          )
        }

        return uncompressedMembers
      } catch (err) {
        reportError(err)
      }

      return null
    },
    [downloadAttachment],
  )

  const bulkRequestLogs = useCallback(
    async (peerKeys: string[]) => {
      const now = dayjs.utc()
      await executeLegacyUpdate({
        query: `contains(${JSON.stringify(peerKeys)}, _id)`,
        collection: SystemCollections.SMALL_PEER_INFO_COLLECTION,
        updates: [
          {
            path: 'log_requests.device_logs.requested_at',
            value: now.format(),
          },
        ],
      })
    },
    [executeLegacyUpdate],
  )

  const requestLogs = useCallback(
    async (peerKey: string) => {
      const now = dayjs.utc()
      await executeLegacyUpdate({
        query: `_id == "${peerKey}"`,
        collection: SystemCollections.SMALL_PEER_INFO_COLLECTION,
        updates: [
          {
            path: 'log_requests.device_logs.requested_at',
            value: now.format(),
          },
        ],
      })
    },
    [executeLegacyUpdate],
  )

  return {
    accessToken: rpcAccessToken,
    refreshToken,
    bulkRequestLogs,
    getDevices,
    getDeviceByPeerKey,
    fetchLogs,
    requestLogs,
  }
}

function reportError(err: unknown) {
  Sentry.withScope((scope) => {
    scope.setTag('useDeviceRPC', 'Error occurred during device RPC operation')
    Sentry.captureException(err)
  })
}

const LOGS_REQUESTED_AT_PATH = 'log_requests.device_logs.requested_at'
const LOGS_RESPONSES_PATH = 'log_responses.device_logs.logs'

export type Device = {
  peerKey: string
  name: string
  platform: string
  dittoSdkLanguage: string
  dittoSdkVersion: string
  manufacturer: string
  model: string
  serial: string
  lastSeen?: Dayjs
  latestLogsRequest?: Dayjs
  latestLogsAttachmentId?: string
  logResponses?: DeviceLogResponse[]
  metadata: Record<string, unknown>
  diskUsage?: DeviceDiskUsage
  subscriptions?: DeviceSubscriptions
  storeMeta?: DeviceStoreMeta
  logConfig?: DeviceLogConfig
}

export type DeviceDiskUsage = {
  deviceTotal: number
  deviceAvailable: number
  dittoTotal: number
  dittoAttachments: number
  dittoReplication: number
  dittoStore: number
}

export type DeviceSubscriptions = {
  truncated: boolean
  queries: DeviceSubscription[]
}

export type DeviceSubscription = {
  query: string
  // https://github.com/getditto/ditto/issues/14382
  docs?: number
}

export type DeviceStoreCollection = {
  numDocs: number
  // https://github.com/getditto/ditto/issues/14294
  numTombstones?: number
}

export type DeviceStoreMeta = {
  truncated: boolean
  collections: Record<string, DeviceStoreCollection>
}

type DeviceLogLevel = 'INFO' | 'DEBUG' | 'TRACE' | 'WARN' | 'ERROR'
const isDeviceLogLevel = (value: unknown): value is DeviceLogLevel =>
  typeof value === 'string' &&
  ['INFO', 'DEBUG', 'TRACE', 'WARN', 'ERROR'].includes(value)

export type DeviceLogConfig = {
  enabled: boolean
  minimumLevel: 'INFO' | 'DEBUG' | 'TRACE' | 'WARN' | 'ERROR'
}

export type DeviceLogResponse = {
  timestamp: Dayjs
  isLatest: boolean
  attachmentId: string | null
}

export const resultToDevice = (result: LegacyHTTPDocument): Device => {
  const id = result.id
  const lastSeen = dayjs(stringField(result, 'last_updated_at'))
  const latestLogsRequestTimestamp = stringField(result, LOGS_REQUESTED_AT_PATH)
  const latestLogsRequest = dayjs(latestLogsRequestTimestamp)
  const latestLogsAttachmentId = latestLogsRequestTimestamp
    ? logAttachmentId(result, latestLogsRequestTimestamp)
    : undefined

  const logResponses: DeviceLogResponse[] = Object.keys(
    objectField(result, LOGS_RESPONSES_PATH),
  )
    .map((timestamp) => ({
      timestamp: dayJsValue(timestamp),
      isLatest: dayjs(timestamp).isSame(latestLogsRequest),
      attachmentId: logAttachmentId(result, timestamp) || null,
    }))
    .filter(isDeviceLogResponse)
    .sort((a, b) => (a.timestamp.isBefore(b.timestamp) ? 1 : -1))

  const mergedMetdata = {
    ...objectField(result, 'metadata'),
    ...objectField(result, 'peer_metadata'),
  }

  return {
    peerKey: typeof id === 'string' ? id : '',
    name: stringField(result, 'device_name'),
    platform: stringField(result, 'ditto_sdk_platform'),
    dittoSdkLanguage: stringField(result, 'ditto_sdk_language'),
    dittoSdkVersion: stringField(result, 'ditto_sdk_version'),
    manufacturer: stringField(result, 'device_manufacturer'),
    model: stringField(result, 'device_model'),
    serial: stringField(result, 'device_serial'),
    lastSeen: lastSeen.isValid() ? lastSeen : undefined,
    latestLogsRequest: latestLogsRequest.isValid()
      ? latestLogsRequest
      : undefined,
    latestLogsAttachmentId: latestLogsAttachmentId || undefined,
    logResponses,
    metadata: mergedMetdata,
    diskUsage: diskUsage(result) || undefined,
    subscriptions: deviceSubscriptions(result) || undefined,
    storeMeta: deviceStoreMeta(result) || undefined,
    logConfig: logConfig(result) || undefined,
  }
}

export const diskUsage = (
  result: LegacyHTTPDocument,
): DeviceDiskUsage | null => {
  const usageRaw = objectField(result, 'device_disk_usage')

  // If the disk usage is not available, return null. If present, we will assume valid structure
  if (!usageRaw || isEmpty(usageRaw)) {
    return null
  }

  return {
    deviceTotal: numberField(result, 'device_disk_usage.device_total'),
    deviceAvailable: numberField(result, 'device_disk_usage.device_available'),
    dittoTotal: numberField(result, 'device_disk_usage.ditto_total'),
    dittoAttachments: numberField(
      result,
      'device_disk_usage.ditto_attachments',
    ),
    dittoReplication: numberField(
      result,
      'device_disk_usage.ditto_replication',
    ),
    dittoStore: numberField(result, 'device_disk_usage.ditto_store'),
  }
}

export const deviceSubscriptions = (
  result: LegacyHTTPDocument,
): DeviceSubscriptions | null => {
  const subscriptionsRaw = objectField(result, 'local_subscriptions')

  // If the subscriptions are not available, return null. If present, we will assume valid structure
  if (!subscriptionsRaw || !Array.isArray(subscriptionsRaw.queries)) {
    return null
  }

  const truncated = Boolean(subscriptionsRaw.truncated) || false
  const queries = subscriptionsRaw.queries
    .map((query) => {
      const queryRaw = getProperty(query, 'query')
      const docs = getProperty(query, 'docs')
      if (queryRaw) {
        return {
          query: queryRaw,
          docs: typeof docs === 'number' ? docs : undefined,
        }
      } else {
        return null
      }
    })
    .filter(Boolean) as DeviceSubscription[]

  return {
    truncated,
    queries,
  }
}

export const deviceStoreMeta = (
  result: LegacyHTTPDocument,
): DeviceStoreMeta | null => {
  const storeMetaRaw = objectField(result, 'store')

  // If the store meta is not available, return null. If present, we will assume valid structure
  if (!storeMetaRaw || isEmpty(storeMetaRaw)) {
    return null
  }

  const truncated = Boolean(storeMetaRaw.truncated) || false
  const userCollections = objectField(result, 'store.user_collections')
  const collections = Object.keys(userCollections).reduce(
    (acc, key) => ({
      ...acc,
      [key]: {
        numDocs: numberField(result, `store.user_collections.${key}.num_docs`),
        numTombstones: numberField(
          result,
          `store.user_collections.${key}.num_tombstones`,
        ),
      },
    }),
    {} as Record<string, DeviceStoreCollection>,
  )

  return {
    truncated,
    collections,
  }
}

export const logConfig = (
  result: LegacyHTTPDocument,
): DeviceLogConfig | null => {
  const logConfigRaw = objectField(result, 'logs_config')

  // If the log config is not available, return null. If present, we will assume valid structure
  if (!logConfigRaw || isEmpty(logConfigRaw)) {
    return null
  }

  const enabled = booleanField(result, 'logs_config.enabled')
  const minimumLevel = stringField(result, 'logs_config.minimum_level')

  return {
    enabled,
    minimumLevel: isDeviceLogLevel(minimumLevel) ? minimumLevel : 'INFO',
  }
}

const numberField = (
  result: LegacyHTTPDocument,
  path: string,
  defaultIfMissing = 0,
): number => {
  const value = getProperty(result.fields, path)
  return typeof value === 'number' ? value : defaultIfMissing
}

const booleanField = (result: LegacyHTTPDocument, path: string): boolean => {
  const value = getProperty(result.fields, path)
  return typeof value === 'boolean' ? value : Boolean(value)
}

const stringField = (result: LegacyHTTPDocument, path: string): string => {
  const value = getProperty(result.fields, path)
  return typeof value === 'string' ? value : ''
}

const objectField = (
  result: LegacyHTTPDocument,
  path: string,
): Record<string, unknown> => {
  const value = getProperty(result.fields, path)
  return typeof value === 'object' ? (value as Record<string, unknown>) : {}
}

const dayJsValue = (date: string) => {
  const value = dayjs(date)
  return value.isValid() ? value : undefined
}

const logAttachmentId = (
  result: LegacyHTTPDocument,
  timestamp: string,
): string | undefined => {
  const value = objectField(
    result,
    `${LOGS_RESPONSES_PATH}["${timestamp}"]`,
  )?.id
  return typeof value === 'string' ? value : undefined
}

export function isDeviceLogResponse<T>(
  value: T | DeviceLogResponse,
): value is DeviceLogResponse {
  return (
    value !== null &&
    typeof value === 'object' &&
    'attachmentId' in value &&
    'timestamp' in value
  )
}

/** The time in ms to refetch devices in a pending log request state */
export const REFETCH_INTERVAL_MS = 5000
/** The number of days until the Portal will no longer poll for changes */
export const LOGS_REQUESTED_CUTOFF_DAYS = 2

/**
 * A function to determine the refetch interval based on the device's latest logs request
 * pending status. If the logs are pending, and haven't surpassed the cutoff days, then
 * the client will refetch each interval tick. Otherwise, the client will not refetch (i.e. return false)
 */
export const shouldRefetch = (device?: Device | null) => {
  if (!device) {
    return false
  }
  const { latestLogsRequest, latestLogsAttachmentId } = device
  const isWithinRange =
    !!latestLogsRequest &&
    dayjs().diff(latestLogsRequest, 'days') <= LOGS_REQUESTED_CUTOFF_DAYS
  if (isWithinRange && !latestLogsAttachmentId) {
    return REFETCH_INTERVAL_MS
  }
  return false
}

/**
 * A function to merge all metadata from a list of devices into a single object.
 * This will try to avoid overwriting existing metadata keys with new values.
 */
export const resolveMetadataFromSampled = (
  devices: Device[],
): Record<string, unknown> => {
  const mergedMetadata: Record<string, unknown> = {}

  devices.forEach(({ metadata }) => {
    Object.entries(metadata).forEach(([key, value]) => {
      const _type = toSupportedJSONPickerType(value)
      if (_type && !mergedMetadata[key]) {
        mergedMetadata[key] = value
      }
    })
  })

  return mergedMetadata
}
